import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Zap, Clock, Shield, Calculator, Truck, FileText, Building2, HardHat, BarChart3 } from 'lucide-react';
import MassMasterLogo from '../components/MassMasterLogo';

const PricingPage = () => {
  const { user, subscription } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  const features = [
    { icon: Calculator, text: 'Demo Material Calculator - Roofing, Siding, Drywall & More' },
    { icon: Building2, text: 'Custom Building Calculator - Complete Structure Estimates' },
    { icon: HardHat, text: 'Oil Field Equipment - Piping & Tank Calculations' },
    { icon: Truck, text: 'Import Volume Calculator - Sand, Gravel, Aggregates' },
    { icon: Calculator, text: 'Excavation Calculator - Natural Material Removal' },
    { icon: Truck, text: 'Export Trucking Calculator - Haul Planning & Costs' },
    { icon: FileText, text: 'Comprehensive Reports - Email Delivery Included' },
    { icon: Shield, text: 'Contractor Search - Find Pros Within 20 Miles' },
    { icon: BarChart3, text: 'Cost Tracking - Per Ton Pricing Throughout' },
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle py-12 px-4 flex flex-col">
      <div className="max-w-5xl mx-auto flex-1">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="mx-auto mb-6">
            <MassMasterLogo size="lg" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Mass Master Pro</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-6">
            The Ultimate Material Weight & Cost Calculator for Construction Professionals
          </p>
        </div>

        {/* App Description */}
        <div className="bg-card border border-border rounded-xl p-8 mb-10 shadow-sm">
          <h2 className="text-2xl font-bold text-foreground mb-4 text-center">What is Mass Master Pro?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-muted-foreground mb-4">
                Mass Master Pro is a comprehensive, professional-grade material weight and cost estimation tool designed specifically for construction, demolition, excavation, and hauling professionals.
              </p>
              <p className="text-muted-foreground mb-4">
                Whether you&apos;re estimating demolition debris, calculating import materials for a job site, planning excavation removal, or determining trucking requirements, Mass Master Pro provides accurate, industry-standard calculations at your fingertips.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-3">Perfect For:</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>General Contractors & Construction Companies</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>Demolition & Excavation Contractors</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>Trucking & Hauling Companies</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>Oil Field Service Companies</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>Project Managers & Estimators</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>Landscape & Earthwork Contractors</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Pricing Card */}
        <Card className="max-w-md mx-auto border-2 border-primary/20 mb-10">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">3-Day Free Trial</span>
            </div>
            <CardTitle className="text-3xl">$9.99<span className="text-lg text-muted-foreground font-normal">/month</span></CardTitle>
            <CardDescription>Full access to all features</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 mb-6">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-sm text-foreground">{feature.text}</span>
                </li>
              ))}
            </ul>

            {subscription?.has_access ? (
              <div className="text-center">
                <div className="bg-green-100 text-green-800 rounded-lg p-3 mb-4">
                  <p className="font-medium">You have active access!</p>
                  <p className="text-sm">
                    {subscription.subscription_status === 'trialing' 
                      ? `Trial ends: ${new Date(subscription.trial_ends_at).toLocaleDateString()}`
                      : `Subscription active until: ${new Date(subscription.subscription_ends_at).toLocaleDateString()}`
                    }
                  </p>
                </div>
                <Button onClick={() => navigate('/')} className="w-full">
                  Go to Calculator
                </Button>
              </div>
            ) : (
              <>
                <Button onClick={handleSubscribe} className="w-full" size="lg">
                  {user ? 'Start Free Trial' : 'Sign Up & Start Trial'}
                </Button>
                <p className="text-center text-xs text-muted-foreground mt-3">
                  Cancel anytime. No commitment required.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Trust badges */}
        <div className="flex items-center justify-center gap-6 mb-10 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Secure Payment
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Cancel Anytime
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Instant Access
          </div>
        </div>
      </div>

      {/* Fine Print Legal Disclaimer at Bottom */}
      <div className="max-w-4xl mx-auto mt-auto pt-8 border-t border-border/50">
        <p className="text-[10px] text-muted-foreground/60 text-center leading-relaxed">
          <strong>DISCLAIMER:</strong> The calculations, estimates, and data provided by Mass Master Pro are intended for informational and planning purposes only. 
          While we strive for accuracy using industry-standard material weights and formulas, all results should be considered approximate estimates. 
          It is the sole responsibility of the user to verify all calculations, measurements, and estimates before making any business, financial, or construction decisions. 
          Users should consult with qualified professionals, engineers, or surveyors for precise measurements and critical project planning. 
          Mass Master Pro, its owners, developers, and affiliates make no representations or warranties of any kind, express or implied, regarding the accuracy, completeness, reliability, or suitability of the calculations provided. 
          The service is provided &quot;as is&quot; without warranty of any kind. Under no circumstances shall Mass Master Pro, its owners, developers, employees, or affiliates be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of or related to the use of this application, including but not limited to errors in calculations, project cost overruns, material shortages, scheduling delays, or any other losses or damages. 
          By using Mass Master Pro, you acknowledge that you have read, understood, and agree to this disclaimer and accept full responsibility for verifying all calculations and for any decisions made based on the information provided by this application.
        </p>
        <p className="text-[10px] text-muted-foreground/60 text-center mt-4">
          © {new Date().getFullYear()} Mass Master Pro™. All Rights Reserved.
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
